import { useMemo } from "react";
import { ProductImages } from "@/components/features/product/product-images";
import { ProductHeader } from "@/components/features/product/product-header";
import { PricingGrid } from "@/components/features/product/pricing-grid";
import { QuantitySelector } from "@/components/features/product/quantity-selector";
import { AddToCart } from "@/components/features/product/add-to-cart";
import { ProductDetails } from "@/components/features/product/product-details";
import { ProductDescription } from "@/components/features/product/product-description";
import { ProductSpecifications } from "@/components/features/product/product-specifications";
import Layout from "@/components/shared/layout/general";

// Hooks
import { useCart } from "@/lib/hooks/cart";
import { usePrice } from "@/lib/hooks/price";
import { useAuth } from "@/lib/hooks/auth";
import { useQuantity } from "@/lib/hooks/quantity";

// Types
import { Product } from "@/components/features/product/product.types";

interface ContentProps {
  product: Product;
}

const Content = ({ product }: ContentProps) => {
  const { 
    quantity, 
    incrementQuantity, 
    decrementQuantity 
  } = useQuantity(1);
  const { authObject, userData } = useAuth();
  const { addItem, cart } = useCart();
  const { getBasePricing, getPrice } = usePrice();

  // Memoize computed values to prevent unnecessary recalculations
  const {
    isInCart,
    isUnverifiedUser,
    adjustedPrice,
    showDiscountedPrice,
    basePricing
  } = useMemo(() => ({
    isInCart: Object.keys(cart).includes(product.FullName),
    isUnverifiedUser: userData?.contact.IsLeadStatusOnly,
    adjustedPrice: getPrice(product.FullName, product.eCommerceCategory, product.SalesPrice),
    showDiscountedPrice: getPrice(product.FullName, product.eCommerceCategory, product.SalesPrice) !== product.SalesPrice,
    basePricing: getBasePricing(product.FullName)
  }), [product, cart, userData, getPrice, getBasePricing]);

  return (
    <div className="bg-white">
      <div className="max-w-2xl mx-auto py-16 px-4 sm:py-24 sm:px-6 lg:max-w-7xl lg:px-8">
        <div className="lg:grid lg:grid-cols-2 lg:gap-x-8 lg:items-end">
          <ProductImages product={product} />
          
          <div className="mt-10 px-4 sm:px-0 sm:mt-16 lg:mt-0">
            <ProductHeader 
              name={product.eCommerceStoreName}
              price={product.SalesPrice}
              adjustedPrice={adjustedPrice}
              showDiscountedPrice={showDiscountedPrice}
            />

            <PricingGrid 
              prices={basePricing}
              category={product.eCommerceCategory}
            />

            <div className="mt-6">
              <h3 className="sr-only">Description</h3>
              <div
                className="text-base text-gray-700 space-y-6"
                dangerouslySetInnerHTML={{ __html: product.eCommerceShortDescription }}
              />
            </div>

            <QuantitySelector 
              quantity={quantity}
              incrementQuantity={incrementQuantity}
              decrementQuantity={decrementQuantity}
              sku={product.FullName}
            />

            <AddToCart 
              isInCart={isInCart}
              isUnverifiedUser={isUnverifiedUser}
              isAuthenticated={!!authObject}
              product={product}
              quantity={quantity}
              onAddToCart={addItem}
            />

            <ProductDetails
              isAuthenticated={!!authObject}
              quantityOnHand={product.QuantityOnHand}
              prefVendor={product.PrefVendor}
              prefVendorId={product.PrefVendor_RecordID}
            />
          </div>
        </div>

        <section className="pt-24">
          <div className="border-t divide-y divide-gray-200">
            <ProductDescription 
              description={product.eCommerceDescription} 
              productName={product.FullName}
            />
            <ProductSpecifications 
              specifications={product.specifications}
              productName={product.FullName}
            />
          </div>
        </section>
      </div>
    </div>
  );
};

interface ProductPageProps {
  product: Product;
}

const ProductPage = ({ product }: ProductPageProps) => (
  <Layout>
    <Content product={product} />
  </Layout>
);

export default ProductPage;

export async function getStaticPaths() {
  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_API_URL}/api/method/products`,
      {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        }
      }
    );
    
    if (!response.ok) {
      return { 
        paths: [], 
        fallback: false 
      };
    }

    const urls = await response.json();
    
    return { 
      paths: urls.map((url: string) => ({ params: { product: url } })),
      fallback: false 
    };
  } catch (error) {
    console.error('Error fetching paths:', error);
    return { 
      paths: [], 
      fallback: false 
    };
  }
}

export async function getStaticProps({ params }: { params: { product: string } }) {
  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_API_URL}/api/method/products?url=${params.product}`,
      {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        }
      }
    );
    
    if (!response.ok) {
      console.error(`API Error: ${response.status} ${response.statusText}`);
      return { 
        notFound: true 
      };
    }

    const data = await response.json();
    
    if (!data || !data[0]) {
      return { 
        notFound: true 
      };
    }

    return { 
      props: { product: data[0] },
      revalidate: 60
    };
  } catch (error) {
    console.error('Error fetching product:', error);
    return { 
      notFound: true 
    };
  }
} 